<div class="w-full h-full min-h-0 flex flex-col gap-4">
  <section class="bg-white rounded-md shadow-custom-2 p-4 flex flex-col gap-4 flex-1 min-h-0">
    <div class="w-full flex items-center justify-between">
        <button class="w-10 h-10 flex items-center justify-center hover:text-nside-orange-400"
          (click)="$event.preventDefault(); onBack();"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left-icon lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
        </button>
        <h2 class="oswald-font uppercase text-2xl text-nside-orange-400">Site Threat Assessment</h2>
        <div class="w-10 h-10"></div>
    </div>
    <ol class="flex items-center w-full">
      @for (section of definition.sections; track section.key; let sectionIndex = $index; let isLast = $last) {
        <li class="flex items-center" [class.w-full]="!isLast">
          <button
            type="button"
            [attr.data-tooltip-target]="'section-tooltip-' + sectionIndex"
            data-tooltip-placement="bottom"
            class="flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-colors"
            [class.bg-nside-orange-400]="currentSectionIndex === sectionIndex"
            [class.text-white]="currentSectionIndex === sectionIndex"
            [class.bg-orange-100]="currentSectionIndex !== sectionIndex && canProceedFromSection(section)"
            [class.text-nside-orange-400]="currentSectionIndex !== sectionIndex && canProceedFromSection(section)"
            [class.bg-gray-100]="!canProceedFromSection(section) && currentSectionIndex !== sectionIndex"
            [class.text-gray-500]="!canProceedFromSection(section) && currentSectionIndex !== sectionIndex"
            [class.cursor-not-allowed]="!canMoveToSection(sectionIndex)"
            [disabled]="!canMoveToSection(sectionIndex)"
            [title]="section.title"
            (click)="goToSection(sectionIndex)"
          >
            {{ sectionIndex + 1 }}
          </button>
          <div
            [id]="'section-tooltip-' + sectionIndex"
            role="tooltip"
            class="absolute z-30 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip"
          >
            {{ section.title }}
            <div class="tooltip-arrow" data-popper-arrow></div>
          </div>
          @if (!isLast) {
            <div
              class="w-full h-1 border-b-4 border-gray-100"
            ></div>
          }
        </li>
      }
    </ol>

    <div class="flex items-center justify-between">
      <h2 class="text-lg font-semibold text-slate-800">{{ currentSection.title }}</h2>
      <span class="text-xs font-medium rounded px-2 py-1 bg-slate-100 text-slate-600 uppercase">
        {{ currentSectionProgressLabel }}
      </span>
    </div>
    <div #scrollContainer class="flex-1 overflow-y-auto pr-1 flex flex-col gap-4">
      @if (currentSection.introKey && introInstructions[currentSection.introKey]) {
        <div class="flex flex-col gap-3">
          <p class="text-sm text-slate-600 leading-6">
            {{ introInstructions[currentSection.introKey] }}
          </p>
          <label class="flex items-start gap-3 rounded border border-slate-200 bg-slate-50 p-3">
            <input
              type="checkbox"
              [formControl]="getIntroConfirmationControl(currentSection)"
              class="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
            />
            <span class="text-sm font-medium text-slate-700">
              I confirm I have read the instructions.
            </span>
          </label>
        </div>
      }

      <div class="flex flex-col gap-5">
        @for (question of currentSection.questions; track question.key; let questionIndex = $index) {
          <article
            class="border border-slate-200 rounded-md p-3"
            [formGroup]="getQuestionGroup(question.key)"
          >
            <div class="flex flex-col gap-2">
              <h3 class="font-semibold text-slate-800">
                {{ questionIndex + 1 }}. {{ getMetaForQuestion(question.key)?.prompt || question.title }}
              </h3>
              <div class="flex flex-col gap-2">
                <div class="flex flex-wrap gap-2">
                  @for (option of getMetaForQuestion(question.key)?.options ?? []; track option.value) {
                    <label
                      class="block cursor-pointer rounded-lg border p-3 transition-colors flex-1 min-w-40"
                      [class.border-orange-500]="getQuestionGroup(question.key).controls.value.value === option.value"
                      [class.bg-orange-50]="getQuestionGroup(question.key).controls.value.value === option.value"
                      [class.border-slate-200]="getQuestionGroup(question.key).controls.value.value !== option.value"
                      [class.bg-white]="getQuestionGroup(question.key).controls.value.value !== option.value"
                    >
                      <div class="flex items-start gap-3">
                        <input
                          type="radio"
                          formControlName="value"
                          [value]="option.value"
                          [attr.name]="'rating-' + question.key"
                          [attr.id]="'rating-' + question.key + '-' + option.value"
                          class="mt-1 h-4 w-4 border-slate-300 text-orange-600 focus:ring-orange-500"
                        />
                        <div class="flex flex-col gap-1">
                          <span class="font-semibold tracking-wide" [class]="getRatingLabelClass(option.value)">
                            {{ formatRatingLabel(option.value) }}
                          </span>
                          <span class="text-xs leading-1 text-slate-700">
                            {{ option.label }}
                          </span>
                        </div>
                      </div>
                    </label>
                  }
                </div>
              </div>
              @if (getQuestionGroup(question.key).controls.consideration.value) {
                <div class="flex flex-col gap-1 rounded border border-slate-500 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  <span class="text-slate-600 underscore font-semibold">Options for Consideration</span>
                  {{ getQuestionGroup(question.key).controls.consideration.value }}
                </div>
              }
              @if (getMoreInfoSections(question.key).length) {
                <div class="flex flex-col gap-2">
                  @for (infoSection of getMoreInfoSections(question.key); track infoSection.title) {
                    <details class="rounded border border-slate-200 bg-slate-50">
                      <summary class="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
                        {{ infoSection.title }}
                      </summary>
                      <div class="border-t border-slate-200 p-3 flex flex-col gap-3">
                        @for (block of infoSection.body; track $index) {
                          <div class="flex flex-col gap-2">
                            <div class="text-sm leading-6 text-slate-700">
                              @for (item of block.items; track $index; let itemIndex = $index) {
                                @if (itemIndex > 0 && block.separator) {
                                  <span>{{ block.separator }}</span>
                                }
                                @if (item.text) {
                                  <span>{{ item.text }}</span>
                                }
                                @if (item.link) {
                                  <a
                                    [href]="item.link"
                                    target="_blank"
                                    rel="noreferrer"
                                    class="inline text-sky-700 underline break-all"
                                  >
                                    {{ item.link }}
                                  </a>
                                }
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </details>
                  }
                </div>
              }
            </div>
          </article>
        }
      </div>
    </div>

    <div class="text-sm flex items-center justify-between pt-2">
      <button
        type="button"
        class="px-4 py-1.5 w-20 rounded-md border border-nside-gray-400 oswald-font text-slate-700 disabled:opacity-40"
        [disabled]="isFirstSection"
        (click)="previousSection()"
      >
        Back
      </button>

      @if (!isLastSection) {
        <button
          type="button"
          class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
          [ngClass]="{
            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canProceedFromSection(currentSection),
            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': canProceedFromSection(currentSection)
          }"
          [disabled]="!canProceedFromSection(currentSection)"
          (click)="nextSection()"
        >
          Next
        </button>
      }

      @if (isLastSection) {
        <button
          type="button"
          class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
          [ngClass]="{
            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canProceedFromSection(currentSection),
            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': canProceedFromSection(currentSection)
          }"
          [disabled]="!canProceedFromSection(currentSection)"
          (click)="submit()"
        >
          Submit
        </button>
      }
    </div>
  </section>

  @if (loading()) {
    <spinner pageName="site-security"></spinner>
  }
</div>

<popup-message
  [type]="messageModal()"
  [message]="popupMessage()"
  (close)="resetMessageModal()"
></popup-message>
